if (!customElements.get('wlsn-filter-clear')) {
  /**
   * Wraps any filter removal trigger (pill, per-filter clear, clear-all).
   * Dispatches a bubbling `wlsn:filter:clear` event with the target URL
   * from the `data-url` attribute. All AJAX logic lives in wlsn-filters.
   */
  class WLSNFilterClear extends HTMLElement {
    constructor() {
      super();
      this.customElements = {
        filterClear: 'wlsn-filter-clear',
      };
      this._onClick = this._onClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeyDown);
    }

    /**
     * @param {MouseEvent} _event
     * @returns {void}
     */
    _onClick(_event) {
      this._dispatchClear();
    }

    /**
     * Handles keyboard activation (Enter / Space).
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    _onKeyDown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this._dispatchClear();
    }

    /**
     * Dispatches `wlsn:filter:clear` with the URL from `data-url`.
     * @returns {void}
     */
    _dispatchClear() {
      const url = this.dataset.url;
      if (!url) return;
      this.dispatchEvent(
        new CustomEvent('wlsn:filter:clear', {
          bubbles: true,
          detail: { url },
        })
      );
    }
  }

  customElements.define('wlsn-filter-clear', WLSNFilterClear);
}

if (!customElements.get('wlsn-filter-apply-all')) {
  /**
   * Triggers application of all staged filter selections.
   * Dispatches a bubbling `wlsn:filter:apply-all` event.
   * wlsn-filters builds the URL from the current form state and
   * performs the full fetch (filters + product grid).
   *
   * Only rendered when the `require_apply` block setting is enabled,
   * which also sets `data-require-apply` on the parent wlsn-filters element.
   */
  class WLSNFilterApplyAll extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeyDown);
    }

    /**
     * @param {MouseEvent} _event
     * @returns {void}
     */
    _onClick(_event) {
      this._dispatchApplyAll();
    }

    /**
     * Handles keyboard activation (Enter / Space).
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    _onKeyDown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this._dispatchApplyAll();
    }

    /**
     * Dispatches `wlsn:filter:apply-all` so wlsn-filters can build
     * the URL from the form and perform the full fetch.
     * @returns {void}
     */
    _dispatchApplyAll() {
      this.dispatchEvent(
        new CustomEvent('wlsn:filter:apply-all', { bubbles: true })
      );
    }
  }

  customElements.define('wlsn-filter-apply-all', WLSNFilterApplyAll);
}

if (!customElements.get('wlsn-filters')) {
  /**
   * Drives AJAX filtering and sorting for the WLSN collection page.
   * Owns the filter form, listens for input changes and clear/apply events,
   * builds URLs, fetches the new page, and swaps DOM for both itself
   * and wlsn-product-facet.
   *
   * When the `data-require-apply` attribute is present:
   * - Checkbox / price changes fetch and update filter counts only
   *   (no URL push, no product grid update) until Apply is clicked.
   * - Sort radio changes still apply immediately (not a staged filter).
   * - wlsn-filter-clear (pills / clear-all) still applies immediately.
   *
   * Without `data-require-apply`, all changes apply immediately (original behaviour).
   */
  class WLSNFilters extends HTMLElement {
    constructor() {
      super();

      this.customElements = {
        filters: 'wlsn-filters',
        productFacet: 'wlsn-product-facet',
        filterClear: 'wlsn-filter-clear',
        filterApplyAll: 'wlsn-filter-apply-all',
        filterBannerCount: 'js-filter-drawer-banner-count',
      };

      this.classes = {
        loading: 'loading',
      };

      this._abortController = null;
      this._priceDebounceTimer = null;
      this._hasPendingChanges = false;

      this._onChange = this._onChange.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onFilterClear = this._onFilterClear.bind(this);
      this._onApplyAll = this._onApplyAll.bind(this);
      this._onPopState = this._onPopState.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this._onChange);
      this.addEventListener('input', this._onInput);
      this.addEventListener('wlsn:filter:clear', this._onFilterClear);
      this.addEventListener('wlsn:filter:apply-all', this._onApplyAll);
      window.addEventListener('popstate', this._onPopState);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('input', this._onInput);
      this.removeEventListener('wlsn:filter:clear', this._onFilterClear);
      this.removeEventListener('wlsn:filter:apply-all', this._onApplyAll);
      window.removeEventListener('popstate', this._onPopState);
      this._abort();
      clearTimeout(this._priceDebounceTimer);
    }

    /**
     * Live DOM references — re-queried on each access so they remain
     * valid after innerHTML replacement.
     * @returns {{ form: HTMLFormElement|null, productFacet: Element|null }}
     */
    get elements() {
      return {
        form: this.querySelector('form'),
        productFacet: document.querySelector(this.customElements.productFacet),
      };
    }

    /**
     * Returns true when the element has the `data-require-apply` attribute,
     * meaning filter changes should stage (filters only) until Apply is clicked.
     * @returns {boolean}
     */
    get _requireApply() {
      return this.hasAttribute('data-require-apply');
    }

    /**
     * Handles change events from checkboxes and sort radios.
     * - All changes stage (filters only) when data-require-apply is set,
     *   otherwise apply immediately.
     * Price inputs are excluded here and handled via _onInput.
     * @param {Event} event
     * @returns {void}
     */
    _onChange(event) {
      if (event.target.hasAttribute('data-price-input')) return;

      const url = this._buildURL();

      if (!this._requireApply) {
        history.pushState({}, '', url);
        this._fetchAndUpdate(url);
      } else {
        this._hasPendingChanges = true;
        this._fetchAndUpdate(url, { updateProducts: false });
      }
    }

    /**
     * Handles input events from price text inputs, debounced 600ms.
     * Stages (filters only) when data-require-apply is set,
     * otherwise applies immediately.
     * @param {Event} event
     * @returns {void}
     */
    _onInput(event) {
      if (!event.target.hasAttribute('data-price-input')) return;
      clearTimeout(this._priceDebounceTimer);
      this._priceDebounceTimer = setTimeout(() => {
        const url = this._buildURL();
        if (this._requireApply) {
          this._hasPendingChanges = true;
          this._fetchAndUpdate(url, { updateProducts: false });
        } else {
          history.pushState({}, '', url);
          this._fetchAndUpdate(url);
        }
      }, 600);
    }

    /**
     * Handles `wlsn:filter:clear` events bubbled from wlsn-filter-clear elements.
     * Always applies immediately regardless of data-require-apply.
     * @param {CustomEvent} event
     * @returns {void}
     */
    async _onFilterClear(event) {
      const { url } = event.detail;
      history.pushState({}, '', url);
      this._hasPendingChanges = false;
      await this._fetchAndUpdate(url);
      this.dispatchEvent(new CustomEvent('wlsn:filter:applied', { bubbles: true }));
    }

    /**
     * Handles `wlsn:filter:apply-all` from wlsn-filter-apply-all.
     * When data-require-apply is absent: products are already updated — just closes the drawer.
     * When data-require-apply is present: full fetch + URL push, then closes the drawer.
     * @returns {void}
     */
    async _onApplyAll() {
      if (!this._requireApply) {
        this.dispatchEvent(new CustomEvent('wlsn:filter:applied', { bubbles: true }));
        return;
      }
      const url = this._buildURL();
      history.pushState({}, '', url);
      this._hasPendingChanges = false;
      await this._fetchAndUpdate(url);
      this.dispatchEvent(new CustomEvent('wlsn:filter:applied', { bubbles: true }));
    }

    /**
     * Handles browser back / forward navigation.
     * @returns {void}
     */
    _onPopState() {
      this._fetchAndUpdate(window.location.href);
    }

    /**
     * Builds a URL from the current form state.
     * Strips empty price params and the page param (filter changes return to page 1).
     * @returns {string}
     */
    _buildURL() {
      const { form } = this.elements;
      if (!form) return window.location.href;

      const params = new URLSearchParams(new FormData(form));

      if (params.get('filter.v.price.gte') === '') params.delete('filter.v.price.gte');
      if (params.get('filter.v.price.lte') === '') params.delete('filter.v.price.lte');
      params.delete('page');

      const url = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of params) {
        url.searchParams.append(key, value);
      }
      return url.toString();
    }

    /**
     * Fetches the given URL and swaps innerHTML for this element and,
     * optionally, wlsn-product-facet.
     * @param {string} url
     * @param {{ updateProducts?: boolean }} [options]
     * @returns {Promise<void>}
     */
    async _fetchAndUpdate(url, { updateProducts = true } = {}) {
      const activePanelKeys = new Set(
        [...this.querySelectorAll('wlsn-filter-panel.active')]
          .map(el => el.dataset.filterKey)
          .filter(Boolean)
      );

      this._abort();
      this._setLoading(true, updateProducts);

      try {
        this._abortController = new AbortController();
        const res = await fetch(url, { signal: this._abortController.signal });

        if (!res.ok) throw new Error(`[wlsn-filters] fetch failed: ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        if (updateProducts) {
          const { productFacet } = this.elements;
          const newFacet = doc.querySelector(this.customElements.productFacet);
          if (productFacet && newFacet) {
            productFacet.innerHTML = newFacet.innerHTML;
          }

          const newCount = doc.querySelector(`.${this.customElements.filterBannerCount}`);
          const currentCount = document.querySelector(`.${this.customElements.filterBannerCount}`);
          if (newCount && currentCount) {
            currentCount.innerHTML = newCount.innerHTML;
          }
        }

        const newFilters = doc.querySelector(this.customElements.filters);
        if (newFilters) {
          this.innerHTML = newFilters.innerHTML;
          this.querySelectorAll('wlsn-filter-panel').forEach(el => {
            if (activePanelKeys.has(el.dataset.filterKey)) {
              el.classList.add('active');
            }
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[wlsn-filters]', error);
        }
      } finally {
        this._setLoading(false, updateProducts);
      }
    }

    /**
     * Cancels any in-flight fetch request.
     * @returns {void}
     */
    _abort() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
    }

    /**
     * Toggles the loading class on this element and, when updateProducts is true,
     * on wlsn-product-facet.
     * @param {boolean} isLoading
     * @param {boolean} updateProducts
     * @returns {void}
     */
    _setLoading(isLoading, updateProducts = true) {
      this.classList.toggle(this.classes.loading, isLoading);
      if (updateProducts) {
        this.elements.productFacet?.classList.toggle(this.classes.loading, isLoading);
      }
    }
  }

  customElements.define('wlsn-filters', WLSNFilters);
}

if (!customElements.get('wlsn-filter-panel')) {
  class WLSNFilterPanel extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        toggle: '[data-filter-panel-toggle]',
      };

      this._onClick = this._onClick.bind(this);
    }

    /**
     * @returns {{ toggle: HTMLButtonElement|null }}
     */
    get elements() {
      return {
        toggle: this.querySelector(this.classes.toggle),
      };
    }

    connectedCallback() {
      this.elements.toggle?.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.elements.toggle?.removeEventListener('click', this._onClick);
    }

    /**
     * @param {MouseEvent} _event
     * @returns {void}
     */
    _onClick(_event) {
      const isActive = this.classList.contains(this.classes.active);
      this.classList.toggle(this.classes.active, !isActive);
    }
  }

  customElements.define('wlsn-filter-panel', WLSNFilterPanel);
}

if (!customElements.get('wlsn-filter-drawer')) {
  class WLSNFilterDrawer extends HTMLElement {
    constructor() {
      super();

      this.customElements = {
        filterDrawer: 'wlsn-filter-drawer',
      };

      this.classes = {
        active: 'active',
        toggle: '[data-wlsn-drawer-toggle]',
        lockScroll: 'wlsn-lock-scroll',
        overlay: 'js-filter-drawer-overlay',
      };

      this._onKeyDown = this._onKeyDown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onFilterApplied = this._onFilterApplied.bind(this);
    }

    /**
     * Live DOM references — re-queried on each access so they remain
     * valid after innerHTML replacement.
     * @returns {{ closeBtn: Element|null, overlay: Element|null }}
     */
    get elements() {
      return {
        toggle: document.querySelectorAll(this.classes.toggle),
        overlay: this.querySelector(`.${this.classes.overlay}`),
      };
    }

    connectedCallback() {
      this.elements.toggle.forEach((el) => {
        el.addEventListener('click', this._onClick);
      });
      this.addEventListener('wlsn:filter:applied', this._onFilterApplied);
      document.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      this.elements.toggle.forEach((el) => {
        el.removeEventListener('click', this._onClick);
      });
      this.removeEventListener('wlsn:filter:applied', this._onFilterApplied);
      document.removeEventListener('keydown', this._onKeyDown);
    }

    /**
     * Toggles the drawer open/closed.
     * @returns {void}
     */
    _onClick() {
      const { toggle } = this.elements;
      if (!toggle) return;
      const isOpen = this.classList.contains(this.classes.active);
      this._toggle(!isOpen);
    }

    /**
     * Toggles the drawer open/closed.
     * When closing with pending (unapplied) changes, resets filters to the current URL.
     * @param {boolean} isOpen
     * @returns {void}
     */
    _toggle(isOpen) {
      if (!isOpen) {
        const filters = this.querySelector('wlsn-filters');
        if (filters?._hasPendingChanges) {
          filters._hasPendingChanges = false;
          filters._fetchAndUpdate(window.location.href, { updateProducts: false });
        }
      }
      const { overlay } = this.elements;
      this.classList.toggle(this.classes.active, isOpen);
      document.body.classList.toggle(this.classes.lockScroll, isOpen);
      overlay?.classList.toggle(this.classes.active, isOpen);
    }

    /**
     * Closes the drawer after filters have been applied or cleared.
     * @returns {void}
     */
    _onFilterApplied() {
      this._toggle(false);
    }

    /**
     * Handles the Escape key.
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    _onKeyDown(event) {
      if (event.key === 'Escape' && this.classList.contains(this.classes.active)) {
        this._toggle(false);
      }
    }
  }

  customElements.define('wlsn-filter-drawer', WLSNFilterDrawer);
}
